import type { AccountResponse } from "@ccflare/api";
import type { ApiKeyProvider, OAuthProvider } from "@ccflare/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../api";
import { queryKeys } from "../lib/query-keys";
import {
	useAccounts,
	useGroups,
	useRenameAccount,
	useSetAccountGroup,
} from "./queries";
import { useApiError } from "./useApiError";

/**
 * Page-model hook for the Accounts page.
 *
 * Owns: query reads, all mutations, invalidation, error shaping.
 * Components only receive shaped data and call actions.
 */
export function useAccountsPageModel() {
	const { formatError } = useApiError();
	const queryClient = useQueryClient();
	const {
		data: accounts,
		isLoading: loading,
		error: queryError,
	} = useAccounts();
	const { data: groups } = useGroups();
	const renameAccountMutation = useRenameAccount();
	const setAccountGroupMutation = useSetAccountGroup();

	const [actionError, setActionError] = useState<string | null>(null);

	const invalidateAccounts = () =>
		queryClient.invalidateQueries({ queryKey: queryKeys.accounts() });

	// -- Mutations --

	const createApiKeyAccount = useMutation({
		mutationFn: async (params: {
			name: string;
			provider: ApiKeyProvider;
			apiKey: string;
			groupId?: string;
		}) => {
			const result = await api.createApiKeyAccount(params);
			// If a non-default group was requested, assign it via the update endpoint
			if (params.groupId && result.accountId) {
				await api.setAccountGroup(result.accountId, params.groupId);
			}
			return result;
		},
		onSuccess: () => {
			setActionError(null);
			invalidateAccounts();
		},
		onError: (err) => setActionError(formatError(err)),
	});

	const startOAuth = async (params: {
		name: string;
		provider: OAuthProvider;
		accountId?: string;
	}) => {
		try {
			const result = await api.initAddAccount(params);
			setActionError(null);
			return result;
		} catch (err) {
			setActionError(formatError(err));
			throw err;
		}
	};

	const completeOAuth = async (params: {
		provider: OAuthProvider;
		sessionId: string;
		code: string;
	}) => {
		try {
			await api.completeAddAccount(params);
			await invalidateAccounts();
			setActionError(null);
		} catch (err) {
			setActionError(formatError(err));
			throw err;
		}
	};

	const getSessionStatus = async (sessionId: string) => {
		try {
			return await api.getAuthSessionStatus(sessionId);
		} catch (err) {
			setActionError(formatError(err));
			throw err;
		}
	};

	const onOAuthCompleted = async () => {
		try {
			await invalidateAccounts();
			setActionError(null);
		} catch (err) {
			setActionError(formatError(err));
			throw err;
		}
	};

	const removeAccount = useMutation({
		mutationFn: (accountId: string) => api.removeAccount(accountId),
		onSuccess: () => {
			setActionError(null);
			invalidateAccounts();
		},
		onError: (err) => setActionError(formatError(err)),
	});

	const renameAccount = async (accountId: string, newName: string) => {
		try {
			await renameAccountMutation.mutateAsync({ accountId, newName });
			setActionError(null);
		} catch (err) {
			setActionError(formatError(err));
		}
	};

	const togglePause = useMutation({
		mutationFn: (account: AccountResponse) =>
			account.paused
				? api.resumeAccount(account.id)
				: api.pauseAccount(account.id),
		onSuccess: () => invalidateAccounts(),
		onError: (err) => setActionError(formatError(err)),
	});

	const moveAccountGroup = async (accountId: string, groupId: string) => {
		try {
			await setAccountGroupMutation.mutateAsync({ accountId, groupId });
			setActionError(null);
		} catch (err) {
			setActionError(formatError(err));
		}
	};

	// -- Computed --

	const displayError = queryError ? formatError(queryError) : actionError;

	return {
		// Data
		accounts,
		loading,
		error: displayError,
		groups,

		// Actions
		createApiKeyAccount: createApiKeyAccount.mutateAsync,
		startOAuth,
		completeOAuth,
		getSessionStatus,
		onOAuthCompleted,
		removeAccount: (accountId: string) => removeAccount.mutateAsync(accountId),
		renameAccount,
		moveAccountGroup,
		togglePause: (account: AccountResponse) => togglePause.mutateAsync(account),
		isRenaming: renameAccountMutation.isPending,
		clearError: () => setActionError(null),
	};
}
